import React, {useState, useMemo, useCallback} from 'react';
import {AlertTriangle, ChevronDown, ChevronRight, Search, X} from 'lucide-react';
import clsx from 'clsx';

import Card from '@/shared/components/Card';
import {formatCatalogPrice, CatalogTestDotRow, CatalogBadge, CatalogPanelCard} from '@/shared/components/catalog';
import type {OrderTestsCatalogResponse, OrderTestsCatalogPanel, OrderTestsCatalogTest, OrderDraftData, CalculatedValue} from '@/shared/types/api';

// --- Selection state types ---

export type SelectionSource = 'profile' | 'panel' | 'manual';

export interface TestSourceInfo {
    source: SelectionSource;
    panelId?: number;
    panelName?: string;
}

export interface CatalogSelection {
    selectedProfileIds: Set<number>;
    selectedPanelIds: Set<number>;
    selectedTestIds: Set<number>;
    panelSource: Map<number, SelectionSource>;
    testSource: Map<number, TestSourceInfo>;
}

export function createEmptySelection(): CatalogSelection {
    return {
        selectedProfileIds: new Set(),
        selectedPanelIds: new Set(),
        selectedTestIds: new Set(),
        panelSource: new Map(),
        testSource: new Map(),
    };
}

// --- Deserialization helper ---

export function deserializeSelection(data: OrderDraftData): CatalogSelection {
    const panelSource = new Map<number, SelectionSource>();
    for (const [key, value] of Object.entries(data.panelSource)) {
        panelSource.set(Number(key), value as SelectionSource);
    }

    const testSource = new Map<number, TestSourceInfo>();
    for (const [key, value] of Object.entries(data.testSource)) {
        testSource.set(Number(key), value as TestSourceInfo);
    }

    return {
        selectedProfileIds: new Set(data.selectedProfileIds),
        selectedPanelIds: new Set(data.selectedPanelIds),
        selectedTestIds: new Set(data.selectedTestIds),
        panelSource,
        testSource,
    };
}

// --- Cascade helpers ---

export function toggleProfile(
    catalog: OrderTestsCatalogResponse,
    prev: CatalogSelection,
    profileId: number
): CatalogSelection {
    const next: CatalogSelection = {
        selectedProfileIds: new Set(prev.selectedProfileIds),
        selectedPanelIds: new Set(prev.selectedPanelIds),
        selectedTestIds: new Set(prev.selectedTestIds),
        panelSource: new Map(prev.panelSource),
        testSource: new Map(prev.testSource),
    };

    const profile = catalog.profiles.find((p) => p.id === profileId);
    if (!profile) return next;

    if (next.selectedProfileIds.has(profileId)) {
        next.selectedProfileIds.delete(profileId);
        for (const panel of profile.panels) {
            if (next.panelSource.get(panel.id) === 'profile') {
                next.selectedPanelIds.delete(panel.id);
                next.panelSource.delete(panel.id);
                for (const test of panel.tests) {
                    const info = next.testSource.get(test.id);
                    if (info?.source === 'profile') {
                        next.selectedTestIds.delete(test.id);
                        next.testSource.delete(test.id);
                    }
                }
            }
        }
    } else {
        next.selectedProfileIds.add(profileId);
        for (const panel of profile.panels) {
            if (!next.selectedPanelIds.has(panel.id)) {
                next.selectedPanelIds.add(panel.id);
                next.panelSource.set(panel.id, 'profile');
            }
            for (const test of panel.tests) {
                if (!test.inAssayCatalog || test.isMissingPrerequisite) continue;
                if (!next.selectedTestIds.has(test.id)) {
                    next.selectedTestIds.add(test.id);
                    next.testSource.set(test.id, {source: 'profile', panelId: panel.id, panelName: panel.name});
                }
            }
        }
    }
    return next;
}

export function togglePanel(
    catalog: OrderTestsCatalogResponse,
    prev: CatalogSelection,
    panelId: number
): CatalogSelection {
    const next: CatalogSelection = {
        selectedProfileIds: new Set(prev.selectedProfileIds),
        selectedPanelIds: new Set(prev.selectedPanelIds),
        selectedTestIds: new Set(prev.selectedTestIds),
        panelSource: new Map(prev.panelSource),
        testSource: new Map(prev.testSource),
    };

    const panel = catalog.panels.find((p) => p.id === panelId);
    if (!panel) return next;

    // Only lock if the panel's owning profile is actually still selected
    if (next.panelSource.get(panelId) === 'profile') {
        const ownerProfile = catalog.profiles.find((pr) => pr.panels.some((pp) => pp.id === panelId));
        if (ownerProfile && next.selectedProfileIds.has(ownerProfile.id)) return next;
        // Profile no longer selected — clear stale source so the panel can be toggled
        next.panelSource.delete(panelId);
    }

    if (next.selectedPanelIds.has(panelId)) {
        next.selectedPanelIds.delete(panelId);
        next.panelSource.delete(panelId);
        for (const test of panel.tests) {
            const info = next.testSource.get(test.id);
            if (info?.source === 'panel') {
                const stillSelected = [...next.selectedPanelIds].some((otherId) => {
                    const other = catalog.panels.find((p) => p.id === otherId);
                    return other?.tests.some((t) => t.id === test.id);
                });
                if (!stillSelected) {
                    next.selectedTestIds.delete(test.id);
                    next.testSource.delete(test.id);
                }
            }
        }
    } else {
        next.selectedPanelIds.add(panelId);
        next.panelSource.set(panelId, 'panel');
        for (const test of panel.tests) {
            if (!test.inAssayCatalog || test.isMissingPrerequisite) continue;
            if (!next.selectedTestIds.has(test.id)) {
                next.selectedTestIds.add(test.id);
                next.testSource.set(test.id, {source: 'panel', panelId: panel.id, panelName: panel.name});
            }
        }
    }
    return next;
}

export function toggleTest(
    prev: CatalogSelection,
    testId: number
): CatalogSelection {
    const next: CatalogSelection = {
        selectedProfileIds: new Set(prev.selectedProfileIds),
        selectedPanelIds: new Set(prev.selectedPanelIds),
        selectedTestIds: new Set(prev.selectedTestIds),
        panelSource: new Map(prev.panelSource),
        testSource: new Map(prev.testSource),
    };

    const info = next.testSource.get(testId);
    if (info?.source === 'profile' || info?.source === 'panel') {
        // Only lock if the owning panel/profile is actually still selected
        if (info.panelId && next.selectedPanelIds.has(info.panelId)) return next;
        // Stale source — clear it so the test can be toggled
        next.testSource.delete(testId);
    }

    if (next.selectedTestIds.has(testId)) {
        next.selectedTestIds.delete(testId);
        next.testSource.delete(testId);
    } else {
        next.selectedTestIds.add(testId);
        next.testSource.set(testId, {source: 'manual'});
    }
    return next;
}

// --- Price formatting (delegates to shared util) ---

const formatPrice = formatCatalogPrice;

// --- Collapsible section helper ---

const CollapsibleSection: React.FC<{
    label: string;
    children: React.ReactNode;
}> = ({label, children}) => {
    const [open, setOpen] = useState(false);
    return (
        <div>
            <button
                type="button"
                onClick={() => setOpen(!open)}
                className="flex w-full items-center gap-2 px-3 py-2 text-xs text-gray-400 hover:text-gray-600 transition-colors">
                {open ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                {label}
            </button>
            {open && children}
        </div>
    );
};

// --- Profile section ---

const ProfileItem: React.FC<{
    profile: {id: number; name: string; code: string; panels: OrderTestsCatalogPanel[]; totalAddonPrice: number};
    isSelected: boolean;
    onToggleProfile: () => void;
}> = ({profile, isSelected, onToggleProfile}) => {
    const [expanded, setExpanded] = useState(isSelected);
    const [expandedPanelIds, setExpandedPanelIds] = useState<Set<number>>(new Set());

    // Split into multi-test panels vs single-test (wrapper) panels
    const regularPanels = useMemo(
        () => profile.panels.filter((p) => p.testCount > 1).sort((a, b) => a.name.localeCompare(b.name)),
        [profile.panels]
    );
    const individualTests = useMemo(
        () => profile.panels.filter((p) => p.testCount === 1).sort((a, b) => {
            const aName = a.tests[0]?.name ?? a.name;
            const bName = b.tests[0]?.name ?? b.name;
            return aName.localeCompare(bName);
        }),
        [profile.panels]
    );

    const totalTests = useMemo(() => {
        const ids = new Set<number>();
        for (const p of profile.panels) for (const t of p.tests) ids.add(t.id);
        return ids.size;
    }, [profile.panels]);

    const togglePanelExpanded = (panelId: number) => {
        setExpandedPanelIds((prev) => {
            const next = new Set(prev);
            if (next.has(panelId)) next.delete(panelId);
            else next.add(panelId);
            return next;
        });
    };

    return (
        <div>
            <div className={clsx(
                'flex items-center gap-3 rounded-md px-4 py-2.5 transition-colors',
                isSelected
                    ? 'border border-mld-info/20 border-l-[3px] border-l-blue-500 bg-mld-info/5/60'
                    : 'border border-gray-200 bg-gray-50 hover:bg-gray-100'
            )}>
                <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={onToggleProfile}
                    className="h-4 w-4 rounded border-gray-300 text-mld-info focus:ring-blue-500"
                />
                <button
                    type="button"
                    onClick={() => setExpanded(!expanded)}
                    className="flex flex-1 items-center justify-between text-left">
                    <div>
                        <div className="flex items-center gap-1.5">
                            <span className={clsx('text-sm font-semibold', isSelected ? 'text-mld-info' : 'text-gray-800')}>
                                {profile.name}
                            </span>
                            {profile.panels.some((p) => p.tests.some((t) => !t.inAssayCatalog || t.isMissingPrerequisite)) && (
                                <span title="Profile contains panels with issues"><AlertTriangle className="h-3.5 w-3.5 shrink-0 text-mld-danger" /></span>
                            )}
                            {profile.code && <span className="ml-2 text-xs text-gray-400">({profile.code})</span>}
                        </div>
                        <div className="text-[10px] text-gray-400">
                            {profile.panels.length} panel{profile.panels.length === 1 ? '' : 's'} · {totalTests} test{totalTests === 1 ? '' : 's'}
                        </div>
                    </div>
                    <div className="flex items-center gap-3">
                        <span className="font-mono text-sm font-semibold text-mld-primary">
                            {formatPrice(profile.totalAddonPrice)}
                        </span>
                        {expanded
                            ? <ChevronDown className="h-4 w-4 text-gray-400" />
                            : <ChevronRight className="h-4 w-4 text-gray-400" />}
                    </div>
                </button>
            </div>

            {expanded && (
                <div className="ml-6 mt-1.5 mb-2">
                    {/* ---- INCLUDED PANELS ---- */}
                    {regularPanels.length > 0 && (
                        <div>
                            <p className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-gray-400">
                                Included Panels{' '}
                                <span className="normal-case tracking-normal">{regularPanels.length}</span>
                            </p>
                            <div className="space-y-1">
                                {regularPanels.map((panel) => (
                                    <CatalogPanelCard
                                        key={panel.id}
                                        panel={panel}
                                        expanded={expandedPanelIds.has(panel.id)}
                                        onToggleExpand={() => togglePanelExpanded(panel.id)}
                                        showAlertIcon
                                    />
                                ))}
                            </div>
                        </div>
                    )}

                    {/* ---- INDIVIDUAL TESTS ---- */}
                    {individualTests.length > 0 && (
                        <div className={regularPanels.length > 0 ? 'mt-3 border-t border-gray-200 pt-3' : ''}>
                            <p className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-gray-400">
                                Individual Tests{' '}
                                <span className="normal-case tracking-normal">{individualTests.length}</span>
                            </p>
                            <div className="space-y-0.5">
                                {individualTests.map((wrapper) => {
                                    const test = wrapper.tests[0];
                                    if (!test) return null;
                                    return <CatalogTestDotRow key={wrapper.id} test={test} showAlertIcon />;
                                })}
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

// --- Panel section ---

const PanelItem: React.FC<{
    panel: OrderTestsCatalogPanel;
    isSelected: boolean;
    source: SelectionSource | undefined;
    locked: boolean;
    defaultExpanded: boolean;
    onTogglePanel: () => void;
}> = ({panel, isSelected, source, locked, defaultExpanded, onTogglePanel}) => {
    const [expanded, setExpanded] = useState(defaultExpanded);

    const isCascade = source === 'profile';
    const isManual = source === 'panel';

    const variant = isSelected && isCascade ? 'cascade' as const
        : isSelected && isManual ? 'manual' as const
        : 'none' as const;

    const badge = isSelected && isCascade
        ? <CatalogBadge label="via Profile" variant="cascade" />
        : isSelected && isManual
            ? <CatalogBadge label="Add-On" variant="manual" />
            : undefined;

    return (
        <CatalogPanelCard
            panel={panel}
            expanded={expanded}
            onToggleExpand={() => setExpanded(!expanded)}
            showCheckbox
            checked={isSelected}
            disabled={locked}
            onCheck={onTogglePanel}
            badge={badge}
            selectionVariant={variant}
            showAlertIcon
        />
    );
};

// --- Main Component ---

interface TestCatalogProps {
    catalog: OrderTestsCatalogResponse;
    selection: CatalogSelection;
    autoAddedCalcValues?: CalculatedValue[];
    onToggleProfile: (profileId: number) => void;
    onTogglePanel: (panelId: number) => void;
    onToggleTest: (testId: number) => void;
    onSelectCalcValue?: (code: string) => void;
}

type TestFilter = 'all' | 'selected' | 'unselected';

const TestCatalog: React.FC<TestCatalogProps> = ({catalog, selection, autoAddedCalcValues = [], onToggleProfile, onTogglePanel, onToggleTest, onSelectCalcValue}) => {
    const [testFilter, setTestFilter] = useState<TestFilter>('all');
    const [testSearch, setTestSearch] = useState('');

    // --- Profiles: selected first, then unselected ---
    const selectedProfiles = catalog.profiles.filter((p) => selection.selectedProfileIds.has(p.id));
    const unselectedProfiles = catalog.profiles.filter((p) => !selection.selectedProfileIds.has(p.id));

    // --- Panels: exclude wrapper panels (testCount === 1), selected first ---
    const realPanels = useMemo(() => catalog.panels.filter((p) => p.testCount > 1), [catalog.panels]);
    const selectedPanels = realPanels.filter((p) => selection.selectedPanelIds.has(p.id));
    const unselectedPanels = realPanels.filter((p) => !selection.selectedPanelIds.has(p.id));

    // --- Calculated value lookup maps ---
    const calcValueMap = useMemo(() => {
        const map = new Map<string, CalculatedValue>();
        for (const cv of catalog.calculatedValues) map.set(cv.code, cv);
        return map;
    }, [catalog.calculatedValues]);

    const satisfiedCalcCodes = useMemo(() => {
        const codes = new Set<string>();
        for (const cv of autoAddedCalcValues) codes.add(cv.code);
        return codes;
    }, [autoAddedCalcValues]);

    // --- Individual Tests: all assay catalog tests (same list as admin) ---
    // Tests with id === -1 have no wrapper panel and can't be ordered yet.
    const allTests = useMemo(() => {
        return [...(catalog.assayTests ?? [])].sort((a, b) => a.name.localeCompare(b.name));
    }, [catalog.assayTests]);

    // Map assay codes → test names for prerequisite display
    const testNameByAssayCode = useMemo(() => {
        const map = new Map<string, string>();
        for (const t of allTests) {
            if (t.assayCode) map.set(t.assayCode, t.name);
        }
        return map;
    }, [allTests]);

    // A test is "effectively selected" if manually selected OR auto-satisfied calc value
    // Tests with id <= 0 are unorderable and never count as selected
    const isEffectivelySelected = useCallback((test: OrderTestsCatalogTest): boolean => {
        if (test.id <= 0) return false;
        if (selection.selectedTestIds.has(test.id)) return true;
        if (test.isCalculated) {
            const code = test.assayCode || test.code;
            if (satisfiedCalcCodes.has(code)) return true;
        }
        return false;
    }, [selection.selectedTestIds, satisfiedCalcCodes]);

    const selectedTests = useMemo(() => allTests.filter((t) => isEffectivelySelected(t)), [allTests, isEffectivelySelected]);
    const unselectedTests = useMemo(() => allTests.filter((t) => !isEffectivelySelected(t)), [allTests, isEffectivelySelected]);

    // Single flat list: apply filter + search, selected first when showing all
    const displayedTests = useMemo(() => {
        let tests: OrderTestsCatalogTest[];
        if (testFilter === 'selected') tests = selectedTests;
        else if (testFilter === 'unselected') tests = unselectedTests;
        else tests = [...selectedTests, ...unselectedTests];

        if (testSearch.trim()) {
            const q = testSearch.trim().toLowerCase();
            tests = tests.filter((t) => t.name.toLowerCase().includes(q) || (t.code && t.code.toLowerCase().includes(q)));
        }
        return tests;
    }, [selectedTests, unselectedTests, testFilter, testSearch]);

    return (
        <div className="space-y-6">

            {/* ========== PROFILES ========== */}
            {catalog.profiles.length > 0 && (
                <Card>
                    <Card.Header>
                        <div className="flex items-center gap-2">
                            <Card.Title>Profiles</Card.Title>
                            <span className="inline-flex items-center rounded-full bg-mld-info/10 px-2 py-0.5 text-[10px] font-semibold text-mld-info">
                                {catalog.profiles.length}
                            </span>
                            <span className="text-[10px] text-gray-400">available</span>
                        </div>
                    </Card.Header>
                    <Card.Content>
                        <div className="space-y-2">
                            {/* Selected profiles at top, expanded */}
                            {selectedProfiles.map((profile) => (
                                <ProfileItem
                                    key={profile.id}
                                    profile={profile}
                                    isSelected={true}
                                    onToggleProfile={() => onToggleProfile(profile.id)}
                                />
                            ))}

                            {/* Unselected profiles below, collapsed */}
                            {unselectedProfiles.length > 0 && selectedProfiles.length > 0 && (
                                <div className="border-t border-gray-100 pt-1" />
                            )}
                            {unselectedProfiles.map((profile) => (
                                <ProfileItem
                                    key={profile.id}
                                    profile={profile}
                                    isSelected={false}
                                    onToggleProfile={() => onToggleProfile(profile.id)}
                                />
                            ))}
                        </div>
                    </Card.Content>
                </Card>
            )}

            {/* ========== PANELS ========== */}
            <Card>
                <Card.Header>
                    <div className="flex items-center gap-2">
                        <Card.Title>Panels</Card.Title>
                        <span className="inline-flex items-center rounded-full bg-mld-info/10 px-2 py-0.5 text-[10px] font-semibold text-mld-info">
                            {realPanels.length}
                        </span>
                        <span className="text-[10px] text-gray-400">
                            available · {selectedPanels.length} selected
                        </span>
                    </div>
                </Card.Header>
                <Card.Content>
                    <div className="space-y-1.5">
                        {/* Selected panels at top */}
                        {selectedPanels.map((panel) => {
                            const source = selection.panelSource.get(panel.id);
                            const ownerProfile = source === 'profile'
                                ? catalog.profiles.find((pr) => pr.panels.some((pp) => pp.id === panel.id))
                                : undefined;
                            const lockedByProfile = source === 'profile' && !!ownerProfile && selection.selectedProfileIds.has(ownerProfile.id);
                            return (
                                <PanelItem
                                    key={panel.id}
                                    panel={panel}
                                    isSelected={true}
                                    source={lockedByProfile ? source : (source === 'profile' ? 'panel' : source)}
                                    locked={lockedByProfile}
                                    defaultExpanded={source === 'panel' || (source === 'profile' && !lockedByProfile)}
                                    onTogglePanel={() => onTogglePanel(panel.id)}
                                />
                            );
                        })}

                        {/* Unselected panels — collapsed behind accordion */}
                        {unselectedPanels.length > 0 && (
                            <CollapsibleSection label={`${unselectedPanels.length} more panel${unselectedPanels.length === 1 ? '' : 's'}...`}>
                                <div className="space-y-1.5">
                                    {unselectedPanels.map((panel) => (
                                        <PanelItem
                                            key={panel.id}
                                            panel={panel}
                                            isSelected={false}
                                            source={undefined}
                                            locked={false}
                                            defaultExpanded={false}
                                            onTogglePanel={() => onTogglePanel(panel.id)}
                                        />
                                    ))}
                                </div>
                            </CollapsibleSection>
                        )}
                    </div>
                </Card.Content>
            </Card>

            {/* ========== INDIVIDUAL TESTS ========== */}
            <Card>
                <Card.Header>
                    <div className="flex items-center gap-2">
                        <Card.Title>Individual Tests</Card.Title>
                        <span className="inline-flex items-center rounded-full bg-mld-info/10 px-2 py-0.5 text-[10px] font-semibold text-mld-info">
                            {allTests.length}
                        </span>
                        <span className="text-[10px] text-gray-400">
                            available · {selectedTests.length} selected
                        </span>
                    </div>
                </Card.Header>
                <Card.Content>
                    {/* Search + filter row */}
                    <div className="mb-3 flex items-center gap-3">
                        <div className="relative w-64">
                            <input
                                type="text"
                                value={testSearch}
                                onChange={(e) => setTestSearch(e.target.value)}
                                placeholder="Search tests..."
                                className="w-full rounded-md border border-gray-300 py-1.5 pl-8 pr-7 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                            <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
                            {testSearch && (
                                <button
                                    type="button"
                                    onClick={() => setTestSearch('')}
                                    className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full p-0.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors">
                                    <X className="h-3 w-3" />
                                </button>
                            )}
                        </div>
                        <div className="flex gap-1">
                            {([
                                {key: 'all' as const, label: 'All', count: allTests.length},
                                {key: 'selected' as const, label: 'Selected', count: selectedTests.length},
                                {key: 'unselected' as const, label: 'Unselected', count: unselectedTests.length},
                            ]).map(({key, label, count}) => (
                                <button
                                    key={key}
                                    type="button"
                                    onClick={() => setTestFilter(key)}
                                    className={clsx(
                                        'inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-[11px] font-medium transition-colors',
                                        testFilter === key
                                            ? 'bg-blue-600 text-white'
                                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                    )}>
                                    {label}
                                    <span className={clsx(
                                        'inline-flex items-center justify-center rounded-full px-1.5 text-[9px] font-bold min-w-[18px]',
                                        testFilter === key ? 'bg-white/20 text-white' : 'bg-gray-200 text-gray-500'
                                    )}>
                                        {count}
                                    </span>
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Simple flat list — all tests always visible */}
                    <div className="max-h-[500px] overflow-y-auto">
                        {/* Column headers */}
                        <div className="sticky top-0 z-10 mb-1 flex items-center gap-3 border-b border-gray-200 bg-white px-3 pb-1.5">
                            <input type="checkbox" disabled className="h-3.5 w-3.5 rounded border-transparent opacity-0" />
                            <span className="flex-1 text-[9px] font-semibold uppercase tracking-wider text-gray-400">Test Name</span>
                            <span className="w-20 shrink-0 text-left text-[9px] font-semibold uppercase tracking-wider text-gray-400">Source</span>
                            <span className="w-16 shrink-0 text-right text-[9px] font-semibold uppercase tracking-wider text-gray-400">Price</span>
                        </div>
                        {displayedTests.length > 0 ? (
                            displayedTests.map((test, idx) => {
                                const calcValue = test.isCalculated ? calcValueMap.get(test.assayCode || test.code) : undefined;
                                const isSatisfied = test.isCalculated && satisfiedCalcCodes.has(test.assayCode || test.code);
                                const isManuallySelected = test.id > 0 && selection.selectedTestIds.has(test.id);
                                const isSelected = isManuallySelected || isSatisfied;

                                // For regular tests, use normal source info and locking
                                const sourceInfo = isManuallySelected ? selection.testSource.get(test.id) : undefined;
                                const testLocked = isManuallySelected && (sourceInfo?.source === 'profile' || sourceInfo?.source === 'panel')
                                    && !!sourceInfo?.panelId && selection.selectedPanelIds.has(sourceInfo.panelId);

                                // For calculated tests: toggle prerequisites via onSelectCalcValue
                                const handleToggle = () => {
                                    if (test.isCalculated && onSelectCalcValue) {
                                        onSelectCalcValue(test.assayCode || test.code);
                                    } else {
                                        onToggleTest(test.id);
                                    }
                                };

                                // Resolve prerequisite names for calculated tests
                                const prereqNames = calcValue?.prerequisites.map(
                                    (code) => testNameByAssayCode.get(code) || code
                                );

                                return (
                                    <TestTableRow
                                        key={test.id > 0 ? test.id : `${test.code}-${idx}`}
                                        test={test}
                                        isSelected={isSelected}
                                        sourceInfo={sourceInfo ?? (isSatisfied ? {source: 'manual'} : undefined)}
                                        locked={testLocked}
                                        onToggle={handleToggle}
                                        highlight={isSelected && idx % 2 === 0}
                                        calcSatisfied={isSatisfied}
                                        prereqNames={prereqNames}
                                    />
                                );
                            })
                        ) : (
                            <p className="py-4 text-center text-sm text-gray-400">
                                {testSearch ? 'No tests match your search' : 'No tests available'}
                            </p>
                        )}
                    </div>
                </Card.Content>
            </Card>
        </div>
    );
};

// --- Test table row for Individual Tests section ---

const TestTableRow: React.FC<{
    test: OrderTestsCatalogTest;
    isSelected: boolean;
    sourceInfo: TestSourceInfo | undefined;
    locked: boolean;
    onToggle: () => void;
    highlight: boolean;
    calcSatisfied?: boolean;
    prereqNames?: string[];
}> = ({test, isSelected, sourceInfo, locked, onToggle, highlight, calcSatisfied, prereqNames}) => (
    <div>
        <label
            className={clsx(
                'flex items-center gap-3 px-3 py-2 text-sm transition-colors',
                (test.id <= 0 || test.isMissingPrerequisite || !test.inAssayCatalog) && 'opacity-40 cursor-default',
                test.id > 0 && !test.isMissingPrerequisite && test.inAssayCatalog && 'cursor-pointer',
                isSelected && highlight && 'bg-mld-success/5',
                isSelected && !highlight && 'bg-white',
                !isSelected && test.id > 0 && !test.isMissingPrerequisite && test.inAssayCatalog && 'hover:bg-gray-50'
            )}>
            <input
                type="checkbox"
                checked={isSelected}
                onChange={onToggle}
                disabled={locked || test.id <= 0 || test.isMissingPrerequisite || !test.inAssayCatalog}
                className="h-3.5 w-3.5 rounded border-gray-300 text-mld-info focus:ring-blue-500"
            />
            <span className="flex-1 text-xs text-gray-800">
                {test.name || 'Unnamed'}
                {test.isMissingPrerequisite && (
                    <span className="ml-1 text-[10px] font-medium text-mld-danger">(missing prerequisite)</span>
                )}
                {!test.isMissingPrerequisite && !test.inAssayCatalog && (
                    <span className="ml-1 text-[10px] font-medium text-mld-danger">(no catalog match)</span>
                )}
                {!test.isMissingPrerequisite && test.inAssayCatalog && test.id <= 0 && (
                    <span className="ml-1 text-[10px] font-medium text-mld-warning">(not yet orderable)</span>
                )}
            </span>
            <span className="w-20 shrink-0 self-center text-left">
                {isSelected && sourceInfo ? (
                    sourceInfo.source === 'manual' && test.classification === 4 ? (
                        <CatalogBadge label="Send Out" variant="manual" />
                    ) : sourceInfo.source === 'manual' ? (
                        <CatalogBadge label="Add-On" variant="manual" />
                    ) : sourceInfo.source === 'profile' ? (
                        <CatalogBadge label={sourceInfo.panelName ? `via ${sourceInfo.panelName} → Profile` : 'via Profile'} variant="cascade" />
                    ) : (
                        <CatalogBadge label={sourceInfo.panelName ? `via ${sourceInfo.panelName}` : 'via Panel'} variant="manual" />
                    )
                ) : null}
            </span>
            <span className="w-16 shrink-0 text-right font-mono text-[11px]">
                {test.classification === 4 ? (
                    <span className="font-semibold text-mld-warning">TBD</span>
                ) : test.isCalculated ? (
                    <span className="font-semibold text-mld-success">Free</span>
                ) : (
                    <span className="text-gray-600">{formatPrice(test.addonPrice)}</span>
                )}
            </span>
        </label>
        {test.isCalculated && prereqNames && prereqNames.length > 0 && !calcSatisfied && (
            <div className="ml-10 pb-1.5 text-[10px] text-gray-400">
                Requires: {prereqNames.join(', ')}
            </div>
        )}
    </div>
);

export default TestCatalog;
